/**
 * CompetitorTableEditor - Table editor for competitor_id node
 *
 * Displays and allows editing of competitor data stored as JSON array.
 * Read-only table view with raw JSON editing capability.
 */

import { useState, useCallback } from 'react'
import { companyProfileApi, Competitor } from '@services/companyProfileApi'

export interface CompetitorTableEditorProps {
  value: string
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
}

export function CompetitorTableEditor({
  value,
  onSave,
  onCancel,
  saving
}: CompetitorTableEditorProps) {
  const [competitors] = useState<Competitor[]>(() =>
    companyProfileApi.parseCompetitors(value)
  )
  const [editMode, setEditMode] = useState(false)
  const [rawJson, setRawJson] = useState(value || '[]')
  const [jsonError, setJsonError] = useState<string | null>(null)

  const handleSave = useCallback(() => {
    if (editMode) {
      // Validate JSON before saving
      try {
        JSON.parse(rawJson)
        setJsonError(null)
        onSave(rawJson)
      } catch (e) {
        setJsonError('Invalid JSON format')
      }
    } else {
      onSave(value)
    }
  }, [editMode, rawJson, value, onSave])

  const handleEditRaw = useCallback(() => {
    setEditMode(true)
    setRawJson(value || '[]')
  }, [value])

  const handleCancelEdit = useCallback(() => {
    setEditMode(false)
    setJsonError(null)
    setRawJson(value || '[]')
  }, [value])

  if (editMode) {
    return (
      <div className="editor-form">
        <div className="table-editor-info">
          Edit the raw JSON data below. Each competitor should have: name, address,
          distance_mi, rating, reviews, website
        </div>

        {jsonError && (
          <div className="node-editor-error">
            {jsonError}
          </div>
        )}

        <div className="editor-field">
          <label htmlFor="raw-json">Competitor Data (JSON)</label>
          <textarea
            id="raw-json"
            value={rawJson}
            onChange={(e) => {
              setRawJson(e.target.value)
              setJsonError(null)
            }}
            rows={15}
            style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
        </div>

        <div className="editor-buttons">
          <button
            type="button"
            className="editor-btn editor-btn-secondary"
            onClick={handleCancelEdit}
            disabled={saving}
          >
            Back to Table
          </button>
          <button
            type="button"
            className="editor-btn editor-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save JSON'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="table-editor">
      <div className="table-editor-info">
        Competitor data shows nearby competitors. Use "Edit Raw JSON" for manual changes.
      </div>

      {competitors.length === 0 ? (
        <div className="table-editor-empty">
          <p>No competitor data available.</p>
          <p>Use "Edit Raw JSON" to add competitor data manually.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table-editor-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Distance (mi)</th>
                <th>Rating</th>
                <th>Reviews</th>
                <th>Website</th>
              </tr>
            </thead>
            <tbody>
              {competitors.map((comp, idx) => (
                <tr key={idx}>
                  <td>{comp.name || '-'}</td>
                  <td>{comp.address || '-'}</td>
                  <td>{comp.distance_mi ?? '-'}</td>
                  <td>{comp.rating ?? '-'}</td>
                  <td>{comp.reviews ?? '-'}</td>
                  <td>
                    {comp.website ? (
                      <a
                        href={comp.website.startsWith('http') ? comp.website : `https://${comp.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit
                      </a>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="editor-buttons">
        <button
          type="button"
          className="editor-btn editor-btn-secondary"
          onClick={handleEditRaw}
          disabled={saving}
        >
          Edit Raw JSON
        </button>
        <button
          type="button"
          className="editor-btn editor-btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default CompetitorTableEditor
